import React, { useEffect, useMemo, useState } from 'react';

const normalizeApiBase = (rawValue, fallback) => {
  const cleaned = (rawValue || '')
    .trim()
    .replace(/\s+#.*$/, '')
    .replace(/^['\"]|['\"]$/g, '')
    .replace(/;$/, '')
    .replace(/\/+$/, '');

  if (!cleaned) {
    return fallback;
  }

  const withProtocol = /^https?:\/\//i.test(cleaned) ? cleaned : `http://${cleaned}`;

  try {
    return new URL(withProtocol).toString().replace(/\/$/, '');
  } catch {
    return fallback;
  }
};

const buildApiUrl = (base, path) => {
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  try {
    return new URL(normalizedPath, `${base}/`).toString();
  } catch {
    const safeBase = String(base || GROUP_REG_FALLBACK)
      .trim()
      .replace(/^['\"]|['\"]$/g, '')
      .replace(/\/+$/, '');
    return `${safeBase}/${normalizedPath}`;
  }
};

const GROUP_REG_FALLBACK = 'http://localhost:8086';
const CONFERENCE_FALLBACK = 'http://localhost:8080/conference';
const GROUP_REG_API = process.env.NODE_ENV === 'development'
  ? GROUP_REG_FALLBACK
  : normalizeApiBase(process.env.REACT_APP_GROUP_REGISTRATION_API_URL, GROUP_REG_FALLBACK);
const CONFERENCE_API = process.env.NODE_ENV === 'development'
  ? CONFERENCE_FALLBACK
  : normalizeApiBase(process.env.REACT_APP_CONFERENCE_API_URL, CONFERENCE_FALLBACK);

const initialForm = {
  loginEmail: '',
  emailLoginToken: '',
  tokenExpiry: '',
  conferenceGroupId: ''
};

const ConferenceGroupLoginManagement = () => {
  const [logins, setLogins] = useState([]);
  const [groups, setGroups] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [conferenceFilter, setConferenceFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLogin, setEditingLogin] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [alert, setAlert] = useState(null);

  const sortedLogins = useMemo(
    () => [...logins].sort((a, b) => (b.id || 0) - (a.id || 0)),
    [logins]
  );

  const filteredGroups = useMemo(() => {
    if (!conferenceFilter) {
      return groups;
    }
    return groups.filter((group) => group.conference === conferenceFilter);
  }, [groups, conferenceFilter]);

  const conferenceOptions = useMemo(
    () => conferences.map((conference) => conference.name).filter(Boolean),
    [conferences]
  );

  const visibleLogins = useMemo(() => {
    if (!conferenceFilter) {
      return sortedLogins;
    }
    return sortedLogins.filter((login) => login.conferenceGroup?.conference === conferenceFilter);
  }, [sortedLogins, conferenceFilter]);

  useEffect(() => {
    fetchGroups();
    fetchConferences();
  }, []);

  useEffect(() => {
    fetchLogins(groupFilter);
  }, [groupFilter]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4500);
  };

  const fetchGroups = async () => {
    try {
      const response = await fetch(buildApiUrl(GROUP_REG_API, '/group-registration/groups'));
      if (!response.ok) {
        throw new Error('Unable to load conference groups');
      }
      const data = await response.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error instanceof SyntaxError) {
        try {
          const fallbackResponse = await fetch(buildApiUrl(GROUP_REG_FALLBACK, '/group-registration/groups'));
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            setGroups(Array.isArray(fallbackData) ? fallbackData : []);
            return;
          }
        } catch (fallbackError) {
          console.error('Fallback groups fetch failed:', fallbackError);
        }
      }
      console.error('Failed to fetch groups:', error);
      showAlert('Could not load conference groups for dropdown', 'error');
    }
  };

  const fetchConferences = async () => {
    try {
      const response = await fetch(buildApiUrl(CONFERENCE_API, '/active'));
      if (!response.ok) {
        throw new Error('Unable to load conferences');
      }
      const data = await response.json();
      setConferences(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch conferences:', error);
      showAlert('Could not load conferences', 'error');
    }
  };

  const handleConferenceChange = (event) => {
    const selectedConference = event.target.value;
    setConferenceFilter(selectedConference);
    setGroupFilter('');
    setFormData((prev) => ({ ...prev, conferenceGroupId: '' }));
  };

  const fetchLogins = async (conferenceGroupId = '') => {
    setLoading(true);
    try {
      const query = conferenceGroupId ? `?conferenceGroupId=${conferenceGroupId}` : '';
      const response = await fetch(buildApiUrl(GROUP_REG_API, `/group-registration/group-logins${query}`));
      if (!response.ok) {
        throw new Error('Unable to load group logins');
      }
      const data = await response.json();
      setLogins(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch group logins:', error);
      showAlert('Failed to load group logins', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditingLogin(null);
    setFormData({ ...initialForm, conferenceGroupId: groupFilter || '' });
    setShowModal(true);
  };

  const toDateTimeLocal = (rawValue) => {
    if (!rawValue) {
      return '';
    }
    const date = new Date(rawValue);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const openEditModal = (login) => {
    setEditingLogin(login);
    setFormData({
      loginEmail: login.loginEmail || '',
      emailLoginToken: login.emailLoginToken || '',
      tokenExpiry: toDateTimeLocal(login.tokenExpiry),
      conferenceGroupId: String(login.conferenceGroup?.id || '')
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLogin(null);
    setFormData(initialForm);
  };

  const buildPayload = () => ({
    loginEmail: formData.loginEmail.trim(),
    emailLoginToken: formData.emailLoginToken.trim(),
    tokenExpiry: formData.tokenExpiry || null,
    conferenceGroupId: Number(formData.conferenceGroupId)
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.loginEmail.trim() || !formData.emailLoginToken.trim() || !formData.conferenceGroupId) {
      showAlert('Email, token and conference group are required', 'error');
      return;
    }

    const payload = buildPayload();

    try {
      if (!editingLogin) {
        const response = await fetch(buildApiUrl(GROUP_REG_API, '/group-registration/group-logins'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to create login');
        }

        showAlert('Group login created successfully', 'success');
      } else {
        const response = await fetch(buildApiUrl(GROUP_REG_API, `/group-registration/group-logins/${editingLogin.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to update login');
        }

        showAlert('Group login updated successfully', 'success');
      }

      closeModal();
      fetchLogins(groupFilter);
    } catch (error) {
      console.error('Failed to save group login:', error);
      showAlert(error.message || 'Failed to save group login', 'error');
    }
  };

  const handleDelete = async (loginId) => {
    if (!window.confirm('Delete this group login?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(GROUP_REG_API, `/group-registration/group-logins/${loginId}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to delete login');
      }

      showAlert('Group login deleted successfully', 'success');
      fetchLogins(groupFilter);
    } catch (error) {
      console.error('Failed to delete group login:', error);
      showAlert(error.message || 'Delete endpoint not available yet', 'error');
    }
  };

  const handleGenerateToken = async (login) => {
    try {
      const response = await fetch(buildApiUrl(GROUP_REG_API, '/group-registration/group-logins/generate-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loginEmail: login.loginEmail,
          conferenceGroupId: login.conferenceGroup?.id,
          expiryHours: 48
        })
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Token generation failed');
      }

      showAlert('New token generated successfully', 'success');
      fetchLogins(groupFilter);
    } catch (error) {
      console.error('Failed to generate token:', error);
      showAlert(error.message || 'Failed to generate token', 'error');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2>Conference Group Login Management</h2>
          <p style={{ color: '#666', marginTop: '6px' }}>
            Create, update and delete records from conference_group_login.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={conferenceFilter} onChange={handleConferenceChange} style={inputStyle}>
            <option value="">All Conferences</option>
            {conferenceOptions.map((conference) => (
              <option key={conference} value={conference}>
                {conference}
              </option>
            ))}
          </select>
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} style={inputStyle}>
            <option value="">All Conference Groups</option>
            {filteredGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.id} - {group.corporateName}
              </option>
            ))}
          </select>
          <button onClick={openCreateModal} style={primaryBtn}>Add Group Login</button>
        </div>
      </div>

      {alert && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px',
            borderRadius: '6px',
            background: alert.type === 'error' ? '#f8d7da' : '#d1e7dd',
            color: alert.type === 'error' ? '#842029' : '#0f5132',
            border: `1px solid ${alert.type === 'error' ? '#f5c2c7' : '#badbcc'}`
          }}
        >
          {alert.message}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f7f9fc' }}>
            <tr>
              <th style={th}>ID</th>
              <th style={th}>Login Email</th>
              <th style={th}>Conference Group</th>
              <th style={th}>Token</th>
              <th style={th}>Token Expiry</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && visibleLogins.length === 0 && (
              <tr>
                <td colSpan={6} style={emptyCell}>No group logins found.</td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={6} style={emptyCell}>Loading group logins...</td>
              </tr>
            )}
            {visibleLogins.map((login) => (
              <tr key={login.id}>
                <td style={td}>{login.id}</td>
                <td style={td}>{login.loginEmail}</td>
                <td style={td}>{login.conferenceGroup?.id || '-'} {login.conferenceGroup?.corporateName ? `- ${login.conferenceGroup.corporateName}` : ''}</td>
                <td style={td}>{login.emailLoginToken}</td>
                <td style={td}>{login.tokenExpiry || '-'}</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={() => openEditModal(login)} style={secondaryBtn}>Edit</button>
                    <button onClick={() => handleGenerateToken(login)} style={tokenBtn}>Generate Token</button>
                    <button onClick={() => handleDelete(login.id)} style={dangerBtn}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ marginTop: 0 }}>{editingLogin ? 'Edit Group Login' : 'Create Group Login'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <label style={labelStyle}>Login Email
                  <input type="email" name="loginEmail" value={formData.loginEmail} onChange={handleInputChange} style={inputStyle} required />
                </label>
                <label style={labelStyle}>Email Login Token
                  <input name="emailLoginToken" value={formData.emailLoginToken} onChange={handleInputChange} style={inputStyle} required />
                </label>
                <label style={labelStyle}>Token Expiry
                  <input type="datetime-local" name="tokenExpiry" value={formData.tokenExpiry} onChange={handleInputChange} style={inputStyle} />
                </label>
                <label style={labelStyle}>Conference Group
                  <select name="conferenceGroupId" value={formData.conferenceGroupId} onChange={handleInputChange} style={inputStyle} required>
                    <option value="">Select Group</option>
                    {filteredGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.id} - {group.corporateName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                <button type="button" onClick={closeModal} style={secondaryBtn}>Cancel</button>
                <button type="submit" style={primaryBtn}>{editingLogin ? 'Save Changes' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const th = {
  textAlign: 'left',
  padding: '12px',
  fontSize: '13px',
  borderBottom: '1px solid #e9ecef'
};

const td = {
  padding: '12px',
  borderBottom: '1px solid #f1f3f5',
  fontSize: '14px',
  verticalAlign: 'top'
};

const emptyCell = {
  ...td,
  textAlign: 'center',
  color: '#666'
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.35)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalStyle = {
  width: 'min(760px, 92vw)',
  background: '#fff',
  borderRadius: '8px',
  padding: '20px'
};

const formGrid = {
  display: 'grid',
  gap: '12px',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  fontSize: '13px',
  color: '#495057'
};

const inputStyle = {
  padding: '10px',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '14px',
  minWidth: '220px'
};

const primaryBtn = {
  padding: '10px 14px',
  border: 'none',
  borderRadius: '4px',
  background: '#0d6efd',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer'
};

const secondaryBtn = {
  padding: '8px 12px',
  border: '1px solid #d0d7de',
  borderRadius: '4px',
  background: '#fff',
  color: '#212529',
  cursor: 'pointer'
};

const tokenBtn = {
  padding: '8px 12px',
  border: 'none',
  borderRadius: '4px',
  background: '#198754',
  color: '#fff',
  cursor: 'pointer'
};

const dangerBtn = {
  padding: '8px 12px',
  border: 'none',
  borderRadius: '4px',
  background: '#dc3545',
  color: '#fff',
  cursor: 'pointer'
};

export default ConferenceGroupLoginManagement;
